import { FloatingElement } from "@data-display/layout/FloatingElement";
import { TextFieldForm } from "@inputs/hook-form";
import { Box, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { TrainingOfferModality } from "@root/utils/enums/offer";
import { Address } from "@styleguide";
import type { Control } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import { isTrainingOfferAddressRequired } from "../rules";
import type { TTrainingSharedFormFieldValues } from "../types";
import { type ITrainingSharedFormSectionProps, getAddressHelperText } from "./shared";

interface ITrainingSharedFormInformationProps<TForm extends TTrainingSharedFormFieldValues>
  extends ITrainingSharedFormSectionProps<TForm> {
  control: Control<TForm>;
  structureNameLabel: string;
  websiteLabel: string;
  remoteLabel?: string;
}

export const TrainingSharedFormInformation = <TForm extends TTrainingSharedFormFieldValues>({
  control,
  fields,
  structureNameLabel,
  websiteLabel,
  remoteLabel = "L’offre est dispensée à distance, sans lieu d’accueil physique (visio etc...)",
}: ITrainingSharedFormInformationProps<TForm>) => {
  const remote = useWatch({ control, name: fields.remote });
  const required = isTrainingOfferAddressRequired(remote);

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Typography variant="body1SemiBold">Informations utiles </Typography>
      <TextFieldForm
        control={control}
        name={fields.structureName}
        label={
          <Box
            sx={{
              pr: 0.5,
            }}
          >
            {structureNameLabel}
          </Box>
        }
        fullWidth
        size="small"
      />
      <Controller
        control={control}
        name={fields.remote}
        render={({ field: { onChange, value } }) => (
          <FormControlLabel
            sx={{ alignSelf: "start" }}
            control={
              <FloatingElement position="left" slotProps={{ box: { sx: { mr: "38px", ml: -1 } } }}>
                <Checkbox
                  size="small"
                  checked={!!value}
                  onChange={(_, checked) => {
                    if (checked) {
                      onChange(TrainingOfferModality.FULL_REMOTE);
                    } else {
                      onChange(null);
                    }
                  }}
                />
              </FloatingElement>
            }
            label={remoteLabel}
          />
        )}
      />
      <Controller
        control={control}
        name={fields.address}
        render={({ field: { value, onChange }, fieldState: { error }, formState: { errors } }) => (
          <Address
            label={`Lieu d'accueil`}
            value={value}
            onChange={(_, nextValue) => onChange(nextValue)}
            fullWidth
            size="small"
            TextFieldProps={{
              error: !!error,
              helperText: error?.message ?? getAddressHelperText(errors.address),
            }}
          />
        )}
      />
      <TextFieldForm control={control} name={fields.structureWebsite} label={websiteLabel} fullWidth size="small" />
    </Stack>
  );
};
